import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import type { WarehouseAddress } from '../types/shipping';

/**
 * Reads the company's return destination from `users.warehouse_*`.
 *
 * Keyed on `getAdminId(user)` rather than the `user` object itself: `getCurrentUser`
 * selects the signed-in user's own row, and a staff member's row carries no
 * warehouse fields — those live on the admin/company row. Reading them off `user`
 * would be silently blank for every non-admin who opens the Shipping Labels page.
 *
 * This is a read-only view. The address is edited in Settings, and the label
 * server re-reads it independently so a stale form can never misroute a return.
 */
export function useWarehouseAddress() {
  const { user } = useAuth();
  const [warehouseAddress, setWarehouseAddress] = useState<WarehouseAddress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const adminId = getAdminId(user);

  const loadWarehouseAddress = useCallback(async () => {
    if (!adminId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('users')
        .select(`
          warehouse_name,
          warehouse_contact_name,
          warehouse_street,
          warehouse_house_number,
          warehouse_house_number_ext,
          warehouse_postal_code,
          warehouse_city,
          warehouse_country_code,
          warehouse_phone,
          warehouse_email,
          company_name
        `)
        .eq('id', adminId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (!data) {
        setWarehouseAddress(null);
        return;
      }

      setWarehouseAddress({
        name: data.warehouse_name || data.company_name || '',
        contactName: data.warehouse_contact_name || '',
        street: data.warehouse_street || '',
        houseNumber: data.warehouse_house_number || '',
        houseNumberExt: data.warehouse_house_number_ext || '',
        postalCode: data.warehouse_postal_code || '',
        city: data.warehouse_city || '',
        countryCode: data.warehouse_country_code || 'NL',
        phone: data.warehouse_phone || '',
        email: data.warehouse_email || '',
        isConfigured: Boolean(
          data.warehouse_street &&
          data.warehouse_house_number &&
          data.warehouse_postal_code &&
          data.warehouse_city
        ),
      });
    } catch (err) {
      setError('Failed to load warehouse address');
      console.error('Error loading warehouse address:', err);
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    loadWarehouseAddress();
  }, [loadWarehouseAddress]);

  return {
    warehouseAddress,
    loading,
    error,
    refreshWarehouseAddress: loadWarehouseAddress,
    isConfigured: warehouseAddress?.isConfigured || false,
  };
}
